import axios from 'axios';
import { getPhoneAuthToken } from './auth';

// Base URL for the LandView Public API
// Prefer env var; fallback to localhost in dev; else production API.
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:3001/api/public'
    : 'https://landlayoutapi.vercel.app/api/public');

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const USER_LIKES_CACHE_TTL = 30000;
let userLikesCache = {
  token: null,
  data: null,
  timestamp: 0,
  promise: null,
};

const invalidateUserLikesCache = () => {
  userLikesCache = { token: null, data: null, timestamp: 0, promise: null };
};

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    // Attach phone auth token if present
    const token = getPhoneAuthToken();
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    console.error('API request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API response error:', error);
    
    // Handle different error scenarios
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      console.error(`API Error ${status}:`, data);
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received:', error.request);
    } else {
      // Something else happened
      console.error('Request setup error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export const landListingsAPI = {
  /**
   * Get all land listings with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of listings to return (default: 50)
   * @param {number} params.offset - Number of listings to skip (default: 0)
   * @param {string} params.location - Filter by location (partial match)
   * @param {string} params.property_type - Filter by exact property type
   * @param {number} params.min_price - Minimum price filter
   * @param {number} params.max_price - Maximum price filter
   * @param {number} params.min_area - Minimum area filter (sq ft)
   * @param {number} params.max_area - Maximum area filter (sq ft)
   * @returns {Promise} API response
   */
  getAllListings: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add parameters to query string if they exist
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
      
      const queryString = queryParams.toString();
      const url = `/land-listings${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching land listings:', error);
      throw error;
    }
  },

  /**
   * Get a single land listing by ID
   * @param {string} id - Listing ID
   * @returns {Promise} API response
   */
  getListingById: async (id) => {
    try {
      const response = await apiClient.get(`/land-listings/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching land listing ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get available property types
   * @returns {Promise} API response
   */
  getPropertyTypes: async () => {
    try {
      const response = await apiClient.get('/property-types');
      return response.data;
    } catch (error) {
      console.error('Error fetching property types:', error);
      throw error;
    }
  },

  /**
   * Get unique locations/cities from current listings
   * @returns {Promise} API response with unique locations
   */
  getUniqueLocations: async () => {
    try {
      // Fetch all listings to extract unique locations
      const response = await apiClient.get('/land-listings?limit=1000');

      if (response.data.success && response.data.data) {
        // Extract unique locations from only ACTIVE listings
        // Active criteria: approvalStatus === 'approved' and status === 'Available'
        const listings = response.data.data || [];
        const activeListings = listings.filter((l) => {
          const approval = (l.approvalStatus || l.approval_status || '').toString().toLowerCase();
          const status = (l.status || '').toString().toLowerCase();
          const isApproved = approval === 'approved';
          const isAvailable = status === 'available';
          return isApproved && isAvailable;
        });

        const locations = activeListings
          .map(listing => listing.location)
          .filter(location => location && location.trim() !== '')
          .filter((location, index, array) => array.indexOf(location) === index)
          .sort();

        return {
          success: true,
          data: locations
        };
      }

      return {
        success: false,
        data: []
      };
    } catch (error) {
      console.error('Error fetching unique locations:', error);
      throw error;
    }
  },

  /**
   * Health check endpoint
   * @returns {Promise} API response
   */
  healthCheck: async () => {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      console.error('Error checking API health:', error);
      throw error;
    }
  },

  /**
   * Submit CRM lead
   * @param {Object} leadData - Lead information
   * @param {string} leadData.phone - Phone number (required)
   * @param {string} leadData.name - Name (optional)
   * @param {string} leadData.email - Email (optional)
   * @param {string} leadData.property_id - Property ID (optional)
   * @param {string} leadData.source - Lead source (optional)
   * @param {string} leadData.notes - Additional notes (optional)
   * @returns {Promise} API response
   */
  submitCrmLead: async (leadData) => {
    try {
      const response = await apiClient.post('/crm-lead', leadData);
      return response.data;
    } catch (error) {
      console.error('Error submitting CRM lead:', error);
      throw error;
    }
  },

  /**
   * Likes API (requires phone auth token)
   */
  likeProperty: async (propertyId) => {
    const response = await apiClient.post('/property-likes', { property_id: propertyId });
    invalidateUserLikesCache();
    return response.data;
  },
  unlikeProperty: async (propertyId) => {
    const response = await apiClient.delete(`/property-likes/${propertyId}`);
    invalidateUserLikesCache();
    return response.data;
  },
  getUserLikes: async () => {
    const token = getPhoneAuthToken() || '';
    const now = Date.now();

    if (userLikesCache.token === token && userLikesCache.data && now - userLikesCache.timestamp < USER_LIKES_CACHE_TTL) {
      return userLikesCache.data;
    }
    if (userLikesCache.token === token && userLikesCache.promise) {
      return userLikesCache.promise;
    }

    const requestToken = token;
    const request = apiClient.get('/property-likes')
      .then((response) => {
        if (userLikesCache.token === requestToken) {
          userLikesCache.data = response.data;
          userLikesCache.timestamp = Date.now();
        }
        return response.data;
      })
      .finally(() => {
        if (userLikesCache.token === requestToken) {
          userLikesCache.promise = null;
        }
      });

    userLikesCache.token = token;
    userLikesCache.promise = request;
    return request;
  },
  getLikesCount: async (propertyId) => {
    const response = await apiClient.get(`/property-likes/count/${propertyId}`);
    return response.data;
  },

  /**
   * Property views tracking
   */
  trackPropertyView: async (payload) => {
    // payload: { property_id, session_id, duration_seconds?, source? }
    const response = await apiClient.post('/property-views', payload);
    return response.data;
  },
  getPropertyViewAnalytics: async (propertyId, timeframe = 'all') => {
    const response = await apiClient.get(`/property-views/${propertyId}?timeframe=${encodeURIComponent(timeframe)}`);
    return response.data;
  },

  /**
   * Property shares tracking
   */
  incrementShareCount: async (propertyId, shareData = {}) => {
    console.log('[API] Incrementing share count for property:', propertyId, 'with data:', shareData);
    try {
      const response = await apiClient.post(`/property-shares/${propertyId}`, shareData);
      console.log('[API] Share count incremented:', response.data);
      return response.data;
    } catch (error) {
      console.error('[API] Failed to increment share count:', error);
      throw error;
    }
  },
  getShareCount: async (propertyId) => {
    console.log('[API] Fetching share count for property:', propertyId);
    try {
      const response = await apiClient.get(`/property-shares/${propertyId}`);
      console.log('[API] Share count fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('[API] Failed to fetch share count:', error);
      throw error;
    }
  },
};

export default landListingsAPI;
